<template>
  <div>
  <div v-if="showPunchButtons" class="punch-attendance-buttons d-flex align-items-center flex-nowrap">
    <b-nav-item
      v-if="isCompanyWfhToday && !isPunchedIn && !isPunchOutDone"
      class="company-wfh-badge-nav disabled"
      :title="companyWfhTodayTitle || 'Company-Wide WFH Day — punch in/out is still required'"
    >
      <feather-icon size="16" icon="HomeIcon" class="mr-50 text-info" />
      <span class="text-info d-none d-md-inline">Co. WFH</span>
    </b-nav-item>
    <b-nav-item
        v-if="!isPunchOutDone"
      :class="{ 
        'disabled': isPunchedIn || isLoading,
        'punch-in-btn': true,
        'punch-in-valid': isPunchedIn && punchInValidation && punchInValidation.isValid,
        'punch-in-invalid': isPunchedIn && punchInValidation && !punchInValidation.isValid
      }"
      @click="onPunchInClick"
      :title="getPunchInTooltip()"
    >
      <feather-icon
        v-if="isPunchedIn && punchInValidation"
        :icon="punchInValidation.isValid ? 'CheckCircleIcon' : 'XCircleIcon'"
        size="16"
        class="mr-50 punch-nav-icon"
      />
      <b-spinner v-else-if="isLoading" small class="mr-50" />
      <feather-icon
        v-else
        size="16"
        icon="LogInIcon"
        class="mr-50 text-primary punch-nav-icon"
      />
      <span class="text-primary punch-label-text">{{ isLoading ? 'Punching In...' : 'Punch In' }}</span>
        <span v-if="isPunchedIn && punchInTimeFormatted" class="ml-2 punch-in-time">{{ punchInTimeFormatted }}</span>
    </b-nav-item>

    <b-nav-item
      v-if="isPunchedIn"
      :class="{ 
        'disabled': isLoading || isPunchOutLoading,
        'punch-out-btn': true
      }"
        @click="openPunchOutModal"
      :title="isPunchOutLoading ? 'Punching Out...' : (isLoading ? 'Loading...' : 'Punch Out')"
    >
      <b-spinner v-if="isPunchOutLoading" small class="mr-50" />
      <feather-icon
        v-else
        size="14"
        icon="LogOutIcon"
        class="mr-50 text-danger punch-nav-icon punch-out-icon"
      />
      <span class="text-danger punch-label-text">{{ isPunchOutLoading ? 'Punching Out...' : (isLoading ? 'Loading...' : 'Punch Out') }}</span>
    </b-nav-item>
      
      <!-- Display Punch Out time and Working Hours after punch out is done -->
      <b-nav-item
        v-if="isPunchOutDone && (punchOutTimeFormatted || todayWorkingHours)"
        class="punch-out-summary disabled"
        :title="getPunchOutSummaryTooltip()"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="16"
          class="mr-50 text-success punch-nav-icon"
        />
        <span class="text-success punch-label-text">Punched Out</span>
        <span v-if="punchOutTimeFormatted" class="ml-2 punch-out-time">{{ punchOutTimeFormatted }}</span>
        <span v-if="todayWorkingHours" class="ml-2 working-hours d-none d-sm-inline">{{ todayWorkingHours }}</span>
      </b-nav-item>
    </div>

    <!-- Punch Out Confirmation Modal -->
    <b-modal
      id="modal-punch-out-confirm"
      ref="modal-punch-out-confirm"
      title="Punch Out Confirmation"
      :no-close-on-backdrop="isPunchOutLoading"
      :no-close-on-esc="isPunchOutLoading"
      @hidden="resetPunchOutModal"
      size="md"
    >
      <div v-if="punchOutModalData">
        <b-alert variant="info" show class="mb-3">
          <strong>Are you sure you want to Punch out for today?</strong>
        </b-alert>

        <div class="punch-out-details mb-3">
          <div class="detail-row mb-2">
            <strong>Punch In Time:</strong>
            <span class="ml-2">{{ punchOutModalData.punchInTimeFormatted }}</span>
          </div>
          <div class="detail-row mb-2">
            <strong>Current Working Hours:</strong>
            <span class="ml-2" :class="punchOutModalData.hoursClass">
              {{ punchOutModalData.currentWorkingHours }}
            </span>
          </div>
        </div>

        <b-alert 
          v-if="punchOutModalData.warningMessage" 
          :variant="punchOutModalData.warningVariant" 
          show 
          class="mb-0"
        >
          <strong>{{ punchOutModalData.warningTitle }}</strong>
          <p class="mb-0 mt-1">{{ punchOutModalData.warningMessage }}</p>
        </b-alert>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="isPunchOutLoading"
          @click="$refs['modal-punch-out-confirm'].hide()"
        >
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="isPunchOutLoading"
          @click="handlePunchOutConfirm"
        >
          <b-spinner v-if="isPunchOutLoading" small class="mr-1" />
          {{ isPunchOutLoading ? 'Processing...' : 'Yes, Punch Out' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BNavItem,
  BModal,
  BAlert,
  BSpinner,
  BButton,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import punchAttendanceService from '@/services/punch-attendance-service'
import attendanceService from '@/services/attendance-service'

export default {
  components: {
    BNavItem,
    BModal,
    BAlert,
    BSpinner,
    BButton,
    ToastificationContent,
  },
  data() {
    return {
      isPunchedIn: false,
      userData: null,
      deviceInfo: null,
      officeLocation: null, // Will be fetched from backend API
      punchConfig: null, // Punch config from backend API
      todayPunchRecord: null, // Today's punch record from API
      isLoading: false, // Loading state for API calls
      isPunchOutLoading: false, // Loading state for punch-out confirm button
      punchInValidation: null, // Store validation status from punch in
      timeSincePunchIn: null, // Time passed since punch in
      timeSincePunchInInterval: null, // Interval for updating time
      isDevelopmentMode: false, // Flag to detect development/testing environment
      useMockLocation: false, // Flag to use mock location for testing
      punchInTimeFormatted: null, // Formatted punch in time (HH:MM)
      punchOutModalData: null, // Data for punch out confirmation modal
      todayWorkingHours: null, // Working hours for today after punch out
      isPunchOutDone: false, // Flag to track if punch out is done for today
      punchOutTimeFormatted: null, // Formatted punch out time (HH:MM)
      showPunchButtons: false, // Whether to show punch buttons based on punch_required
      isWFHToday: false, // Whether user has approved WFH leave for today
      isCompanyWfhToday: false, // Declared Company-Wide WFH Day (geofence skip; punch still required)
      companyWfhTodayTitle: null,
      isStatusLoading: false, // Loading state for initial checkTodayPunchStatus call (decoupled from punch-in loading)
    }
  },
  mounted() {
    this._isMounted = true
    // Check if user has permission to manage punch_attendance
    // if (!this.$can('read', 'punch_attendance')) {
    //   // Don't initialize anything if user doesn't have permission
    //   return
    // }

    // Detect if we're in development/testing mode (IP address or localhost)
    this.detectDevelopmentMode()

    // Get user data from localStorage
    const userDataString = localStorage.getItem('userData')
    if (userDataString) {
      try {
        this.userData = JSON.parse(userDataString)
      } catch (e) {
        console.error('Failed to parse userData from localStorage:', e)
        this.showPunchButtons = false
        return
      }
    } else {
    }

    // Initialize device info
    this.initializeDeviceInfo()
    
    // Load punch config from API (includes office location, permissions, etc.)
    this.loadPunchConfig()
  },
  methods: {
    isLoggedIn() {
      const expireIn = localStorage.getItem('expireIn')
      if (Math.floor(Date.now() / 1000) > Number(expireIn)) {
          window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Session Expired',
              text: 'Your session has expired. Please log in again.',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          });
          ////console.log("called logout")
          localStorage.clear();
          this.$router.push({ name: "auth-login" });
          return false
      }
      return true
    },
    detectDevelopmentMode() {
      const hostname = window.location.hostname
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
      const isIPAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) // Matches IP addresses like 192.168.1.1
      const isDevelopment = process.env.NODE_ENV === 'development'
      const isNotSecure = window.location.protocol === 'https:'
      
      // Enable development mode if:
      // 1. NODE_ENV is development AND
      // 2. (localhost OR IP address OR not secure context)
      this.isDevelopmentMode = isDevelopment && (isLocalhost || isIPAddress || isNotSecure)
      
      // Use mock location if on IP address (geolocation won't work on IP)
      if (this.isDevelopmentMode && isIPAddress) {
        this.useMockLocation = true
        console.warn('🔧 Development Mode: Testing on IP address detected. Using mock location for geolocation.')
        console.warn('📍 Mock location will use office coordinates with high accuracy.')
      }
    },

    initializeDeviceInfo() {
      const userAgent = navigator.userAgent
      const platform = navigator.platform
      const vendor = navigator.vendor
      
      // Detect device type
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
      const isTablet = /iPad|Android/i.test(userAgent) && !/Mobile/i.test(userAgent)
      const isDesktop = !isMobile && !isTablet
      
      // Additional checks to detect mobile devices even in desktop mode
      const screenWidth = window.screen.width
      const screenHeight = window.screen.height
      const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      const isARMArchitecture = /arm|aarch64|armv7|armv8/i.test(platform) || /arm|aarch64|armv7|armv8/i.test(navigator.userAgent)
      const isSmallScreen = screenWidth < 768 || screenHeight < 768 // Mobile screens are typically smaller
      const isPortraitMobile = (screenWidth < 600 && screenHeight > screenWidth) || (screenHeight < 600 && screenWidth > screenHeight)
      
      // Real desktop check: must pass all criteria
      const isRealDesktop = isDesktop && 
                            !hasTouchScreen && 
                            !isARMArchitecture && 
                            !isSmallScreen && 
                            !isPortraitMobile &&
                            screenWidth >= 1024 // Desktop typically has width >= 1024px
      
      // Detect OS
      let os = 'Unknown'
      if (/Windows/i.test(userAgent)) os = 'Windows'
      else if (/Mac/i.test(userAgent)) os = 'macOS'
      else if (/Linux/i.test(userAgent)) os = 'Linux'
      else if (/Android/i.test(userAgent)) os = 'Android'
      else if (/iOS|iPhone|iPad|iPod/i.test(userAgent)) os = 'iOS'
      
      // Detect browser
      let browser = 'Unknown'
      if (/Chrome/i.test(userAgent) && !/Edge|Edg/i.test(userAgent)) browser = 'Chrome'
      else if (/Firefox/i.test(userAgent)) browser = 'Firefox'
      else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) browser = 'Safari'
      else if (/Edge|Edg/i.test(userAgent)) browser = 'Edge'
      else if (/Opera|OPR/i.test(userAgent)) browser = 'Opera'
      
      // Screen resolution
      const screenResolution = `${window.screen.width}x${window.screen.height}`
      
      // Timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      
      this.deviceInfo = {
        userAgent,
        platform,
        vendor,
        deviceType: isMobile ? 'mobile' : isTablet ? 'tablet' : (isRealDesktop ? 'desktop' : 'mobile'), // Use real desktop check
        isMobile,
        isTablet,
        isDesktop: isRealDesktop, // Use real desktop check instead of basic detection
        isRealDesktop, // Store the real desktop flag
        os,
        browser,
        screenResolution,
        timezone,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        hasTouchScreen,
        isARMArchitecture,
        screenWidth,
        screenHeight,
      }
    },

    updateTimeSincePunchIn(punchInTime) {
      if (!punchInTime) return
      
      const updateTime = () => {
        const now = new Date()
        const punchIn = new Date(punchInTime)
        const diffMs = now - punchIn
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
        const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000)
        
        if (diffHours > 0) {
          this.timeSincePunchIn = `${diffHours}h ${diffMinutes}m`
        } else if (diffMinutes > 0) {
          this.timeSincePunchIn = `${diffMinutes}m ${diffSeconds}s`
        } else {
          this.timeSincePunchIn = `${diffSeconds}s`
        }
      }
      
      updateTime()
      // Update every minute
      if (this.timeSincePunchInInterval) {
        clearInterval(this.timeSincePunchInInterval)
      }
      this.timeSincePunchInInterval = setInterval(updateTime, 60000)
    },

    async loadPunchConfig() {
      
      if (!this.userData?.emp_id) {
        console.warn('Employee ID not found. Cannot load punch config.')
        return
      }

      try {
        
        const response = await punchAttendanceService.getPunchConfig(this.userData.emp_id)
        if (!this._isMounted) return
        
        
        if (response.status === 'OK' && response.data) {
          this.punchConfig = response.data
          // Handle nested metadata structure: response.data.metadata.metadata
          const metadata = response.data.metadata?.metadata || response.data.metadata || {}
          
          
          // Parse office location from metadata (format: "lat, lon")
          if (metadata.office_location) {
            const [lat, lon] = metadata.office_location.split(',').map(coord => parseFloat(coord.trim()))
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
              this.officeLocation = null
            } else {
              this.officeLocation = {
                latitude: lat,
                longitude: lon,
                radius: parseInt(metadata.accuracy) || 500,
                name: 'Office Location'
              }
            }
          }
          
          // Set show punch buttons based on punch_required
          this.showPunchButtons = metadata.punch_required === 'TRUE'
          
          
          // Check if user has WFH leave for today
          await this.checkWFHLeaveForToday()
          // Company-Wide WFH Day: still requires punch; skips geofence only
          await this.checkCompanyWfhForToday()
          
          // Check today's punch status from API
          await this.checkTodayPunchStatus()
          
        } else {
          console.error('Failed to load punch config:', response.message)
          this.showPunchButtons = false
        }
      } catch (error) {
        console.error('Error loading punch config:', error)
        this.showPunchButtons = false
      }
    },

    async checkWFHLeaveForToday() {
      if (!this.userData?.emp_id) return
      
      try {
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
        const response = await punchAttendanceService.getLeaveTypeAndStatusByDateRange(
          this.userData.emp_id,
          today,
          today
        )
        if (!this._isMounted) return
        
        if (response.status === 'OK' && Array.isArray(response.data)) {
          // Check if there's an approved WFH leave for today (case-insensitive)
          this.isWFHToday = response.data.some(leave => {
            const leaveType = leave.leave_type?.toLowerCase?.() || ''
            return ['work from home', 'wfh'].includes(leaveType) && leave.status === 'approved'
          })
          
        }
      } catch (error) {
        console.error('Error checking WFH leave for today:', error)
        this.isWFHToday = false
      }
    },

    /**
     * Company-Wide WFH Day for today — punch-in/out is still required.
     * Uses attendanceService.getCompanyWfhDays() (API or localStorage fallback).
     * Skips office geofence only; does NOT touch personal WFH leave/balance.
     */
    async checkCompanyWfhForToday() {
      this.isCompanyWfhToday = false
      this.companyWfhTodayTitle = null
      try {
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
        const res = await attendanceService.getCompanyWfhDays()
        if (!this._isMounted) return
        const days = (res && res.status === 'OK' && Array.isArray(res.data)) ? res.data : []
        const empSite = this.getEmployeeSiteForCompanyWfh()
        const match = days.find(d => {
          if (!d || !d.date) return false
          const dateStr = String(d.date).slice(0, 10)
          if (dateStr !== today) return false
          const puneYes = String(d.pune || '').toUpperCase() === 'YES'
          const delhiYes = String(d.delhi || '').toUpperCase() === 'YES'
          if ((puneYes && delhiYes) || (!puneYes && !delhiYes)) return true
          if (!empSite) return true
          if (empSite === 'pune') return puneYes
          if (empSite === 'delhi') return delhiYes
          return true
        })
        if (match) {
          this.isCompanyWfhToday = true
          this.companyWfhTodayTitle = match.title
            ? `Company-Wide WFH — ${match.title} (punch still required)`
            : 'Company-Wide WFH Day — punch in/out is still required'
        }
      } catch (error) {
        console.error('Error checking company-wide WFH for today:', error)
        this.isCompanyWfhToday = false
      }
    },

    getEmployeeSiteForCompanyWfh() {
      try {
        const user = this.userData || JSON.parse(localStorage.getItem('userData') || '{}')
        const raw = (
          user.location || user.city || user.office_location ||
          user.base_location || user.work_location || user.site || ''
        ).toString().toLowerCase()
        if (raw.includes('pune')) return 'pune'
        if (raw.includes('delhi')) return 'delhi'
      } catch (e) { /* ignore */ }
      return null
    },

    async checkTodayPunchStatus() {
      if (!this.userData?.emp_id) {
        console.warn('Employee ID not found. Cannot check today punch status.')
        return
      }

      try {
        this.isStatusLoading = true
        const response = await punchAttendanceService.getTodayPunch(this.userData.emp_id)
        if (!this._isMounted) return
        
        if (response.status === 'OK' && response.data && Array.isArray(response.data)) {
          // Check if punch out is done for today
          const todayPunchOut = response.data.find(record => {
            const punchDetails = record.punch_details || {}
            const punchInDetails = punchDetails.punch_in_details
            const punchOutDetails = punchDetails.punch_out_details
            
            if (!punchInDetails || !punchInDetails.punch_in_time || !punchOutDetails?.punch_out_time) {
              return false
            }
            
            const punchDate = this.convertToIST(punchInDetails.punch_in_time)
            const today = this.convertToIST(new Date())
            const isToday = (
              punchDate.getDate() === today.getDate() &&
              punchDate.getMonth() === today.getMonth() &&
              punchDate.getFullYear() === today.getFullYear()
            )
            
            return isToday && !!punchOutDetails?.punch_out_time
          })
          
          if (todayPunchOut) {
            // Punch out is done for today - hide punch in button but show punch out summary
            this.isPunchOutDone = true
            this.isPunchedIn = false
            this.todayPunchRecord = todayPunchOut
            this.punchInValidation = null
            this.timeSincePunchIn = null
            this.punchInTimeFormatted = null
            
            // Extract punch out time and calculate working hours
            const punchDetails = todayPunchOut.punch_details || {}
            const punchOutDetails = punchDetails.punch_out_details
            const punchInDetails = punchDetails.punch_in_details
            
            if (punchOutDetails && punchOutDetails.punch_out_time) {
              this.formatPunchOutTime(punchOutDetails.punch_out_time)
            }
            
            // Calculate working hours from punch in and punch out times
            if (punchInDetails && punchInDetails.punch_in_time && punchOutDetails && punchOutDetails.punch_out_time) {
              const punchInTime = new Date(punchInDetails.punch_in_time)
              const punchOutTime = new Date(punchOutDetails.punch_out_time)
              const diffMs = punchOutTime - punchInTime
              const hours = diffMs / (1000 * 60 * 60)
              this.todayWorkingHours = this.formatWorkingHours(hours)
            } else if (todayPunchOut.hours_completed) {
              // Use hours_completed from database if available
              this.todayWorkingHours = this.formatWorkingHours(todayPunchOut.hours_completed)
            }
            
            localStorage.removeItem('punchInData')
            return
          }
          
          // Find today's punch in record (with punch_in_details but without punch_out_details)
          const todayPunch = response.data.find(record => {
            const punchDetails = record.punch_details || {}
            const punchInDetails = punchDetails.punch_in_details
            const punchOutDetails = punchDetails.punch_out_details
            
            if (!punchInDetails || !punchInDetails.punch_in_time) {
              return false
            }
            
            const punchDate = this.convertToIST(punchInDetails.punch_in_time)
            const today = this.convertToIST(new Date())
            const isToday = (
              punchDate.getDate() === today.getDate() &&
              punchDate.getMonth() === today.getMonth() &&
              punchDate.getFullYear() === today.getFullYear()
            )
            
            // Only consider records without punch out
            return isToday && !punchOutDetails?.punch_out_time
          })

          if (todayPunch) {
            this.todayPunchRecord = todayPunch
            this.isPunchedIn = true
            this.isPunchOutDone = false
            // Store validation status from punch in details
            const punchInDetails = todayPunch.punch_details?.punch_in_details || {}
            this.punchInValidation = punchInDetails.location_validation || null
            // Store in localStorage for consistency
            localStorage.setItem('punchInData', JSON.stringify({
              id: todayPunch.id,
              emp_id: todayPunch.emp_id,
              punch_in_time: punchInDetails.punch_in_time,
              location_validation: this.punchInValidation,
            }))
            // Calculate time since punch in
            this.updateTimeSincePunchIn(punchInDetails.punch_in_time)
            // Format punch in time
            this.formatPunchInTime(punchInDetails.punch_in_time)
          } else {
            this.todayPunchRecord = null
            this.isPunchedIn = false
            this.isPunchOutDone = false
            this.punchInValidation = null
            this.timeSincePunchIn = null
            this.punchInTimeFormatted = null
            localStorage.removeItem('punchInData')
          }
        } else {
          // Invalid/non-OK response: reset state to avoid stale data
          this.isPunchedIn = false
          this.isPunchOutDone = false
        }
      } catch (error) {
        console.error('Error checking today punch status:', error)
        // On error, check localStorage as fallback
        // const punchInDataString = localStorage.getItem('punchInData')
        // if (punchInDataString) {
        //   try {
        //     const punchData = JSON.parse(punchInDataString)
        //     const punchDate = this.convertToIST(punchData.punch_in_time)
        //     const today = this.convertToIST(new Date())
        //     if (
        //       punchDate.getDate() === today.getDate() &&
        //       punchDate.getMonth() === today.getMonth() &&
        //       punchDate.getFullYear() === today.getFullYear()
        //     ) {
        //       this.isPunchedIn = true
        //       this.isPunchOutDone = false
        //     } else {
        //       this.isPunchedIn = false
        //       this.isPunchOutDone = false
        //       this.punchInValidation = null
        //       this.timeSincePunchIn = null
        //       this.punchInTimeFormatted = null
        //       this.todayWorkingHours = null
        //       localStorage.removeItem('punchInData')
        //     }
        //   } catch (e) {
        //     this.isPunchedIn = false
        //     this.punchInValidation = null
        //     this.timeSincePunchIn = null
        //     this.punchInTimeFormatted = null
        //     this.todayWorkingHours = null
        //     localStorage.removeItem('punchInData')
        //   }
        // }
      } finally {
        this.isStatusLoading = false
      }
    },

    validateAccuracy(accuracy) {
      // Parse accuracy to a number in case a formatted string like '100m' is passed
      const parsedAccuracy = typeof accuracy === 'number' ? accuracy : parseInt(accuracy, 10)
      // Use the accuracy threshold from punchConfig metadata; fall back to 101 if not configured
      const metadata = this.punchConfig?.metadata?.metadata || this.punchConfig?.metadata || {}
      const MAX_ACCURACY = parseInt(metadata.accuracy, 10) || 101
      if (parsedAccuracy > MAX_ACCURACY) {
        return {
          isValid: false,
          message: `Location accuracy is ${Math.round(parsedAccuracy)} meters, which exceeds the recommended accuracy of ${MAX_ACCURACY} meters. Please ensure GPS is enabled and you have a clear view of the sky for better location accuracy.`
        }
      }
      return { isValid: true, message: null }
    },

    validateDeviceType() {
      const metadata = this.punchConfig?.metadata?.metadata || this.punchConfig?.metadata || {}
      const deviceType = this.deviceInfo?.deviceType || 'unknown'
      
      // Check if device type is allowed
      const isMobile = deviceType === 'mobile' || deviceType === 'tablet'
      const isDesktop = deviceType === 'desktop'
      
      const allowMobile = metadata.allow_mobile === 'TRUE'
      const allowDesktop = metadata.allow_desktop === 'TRUE'
      
      
      if (isMobile && !allowMobile) {
        return {
          isValid: false,
          message: 'Punch in/out from mobile devices is not allowed for your account. Please use a desktop computer.'
        }
      }
      
      if (isDesktop && !allowDesktop) {
        return {
          isValid: false,
          message: 'Punch in/out from desktop is not allowed for your account. Please use a mobile device.'
        }
      }
      
      
      return { isValid: true, message: null }
    },

    calculateDistance(lat1, lon1, lat2, lon2) {
      // Haversine formula to calculate distance between two coordinates
      const R = 6371e3 // Earth's radius in meters
      const φ1 = (lat1 * Math.PI) / 180
      const φ2 = (lat2 * Math.PI) / 180
      const Δφ = ((lat2 - lat1) * Math.PI) / 180
      const Δλ = ((lon2 - lon1) * Math.PI) / 180

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

      const distance = R * c // Distance in meters
      return distance
    },

    validateLocation(location) {
      
      const metadata = this.punchConfig?.metadata?.metadata || this.punchConfig?.metadata || {}
      // Personal WFH leave, permanent WFH, or Company-Wide WFH Day — skip
      // office geofence only (punch-in/out is still required on WFH days)
      if (metadata.allow_wfm === 'TRUE' || this.isWFHToday || this.isCompanyWfhToday) {
        // Still calculate distance for logging, but don't validate
        let distance = 0
        if (this.officeLocation) {
          distance = Math.round(this.calculateDistance(
            location.latitude,
            location.longitude,
            this.officeLocation.latitude,
            this.officeLocation.longitude
          ))
        }
        return { isValid: true, warning: null, distance: distance, isWFH: true }
      }
      
      // Check if office location validation is required
      if (metadata.allow_ofc !== 'TRUE') {
        // Still calculate distance for logging
        let distance = 0
        if (this.officeLocation) {
          distance = Math.round(this.calculateDistance(
            location.latitude,
            location.longitude,
            this.officeLocation.latitude,
            this.officeLocation.longitude
          ))
        }
        return { 
          isValid: true, 
          warning: null,
          distance: distance,
          skipValidation: true
        }
      }
      
      if (!this.officeLocation) {
        // If office location is not configured, allow punch but log warning
        console.warn('Office location not configured. Geofencing validation skipped.')
        return { isValid: true, warning: null, distance: 0 }
      }

      // GEOFENCING: Calculate actual distance from GPS coordinates to office location
      // DO NOT use location.accuracy - that's GPS uncertainty, not distance from office
      const distance = this.calculateDistance(
        location.latitude,
        location.longitude,
        this.officeLocation.latitude,
        this.officeLocation.longitude
      )

      // GEOFENCING: Validate using calculated distance (NOT accuracy)
      // User must be within office radius to punch in/out
      const isValid = distance <= this.officeLocation.radius
      
      // Warning message for when outside geofence
      const warning = isValid
        ? null
        : `You are ${Math.round(distance)}m away from the office location. ` +
          `Expected location: ${this.officeLocation.name} (within ${this.officeLocation.radius}m). ` +
          `Your current location is outside the office premises.`


      return { isValid, warning, distance: Math.round(distance) }
    },

    async onPunchInClick() {
      if (this.isLoading || this.isPunchedIn || this.isPunchOutDone) return
      this.isLoading = true // Set synchronously before any async work to block rapid double-clicks
      if (!this.isLoggedIn()) {
        this.isLoading = false
        return
      }
      await this.handlePunchIn()
    },
    
    // Convert date to IST (Indian Standard Time) using Intl.DateTimeFormat
    convertToIST(date) {
      if (!date) return null
      const dateObj = date instanceof Date ? date : new Date(date)
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).formatToParts(dateObj)
      const get = (type) => parseInt(parts.find(p => p.type === type)?.value || '0', 10)
      return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
    },
    
    formatPunchInTime(punchInTime) {
      if (!punchInTime) {
        this.punchInTimeFormatted = null
        return
      }
      const istDate = this.convertToIST(punchInTime)
      const hours = String(istDate.getHours()).padStart(2, '0')
      const minutes = String(istDate.getMinutes()).padStart(2, '0')
      this.punchInTimeFormatted = `${hours}:${minutes}`
    },
    
    formatPunchOutTime(punchOutTime) {
      if (!punchOutTime) {
        this.punchOutTimeFormatted = null
        return
      }
      const istDate = this.convertToIST(punchOutTime)
      const hours = String(istDate.getHours()).padStart(2, '0')
      const minutes = String(istDate.getMinutes()).padStart(2, '0')
      this.punchOutTimeFormatted = `${hours}:${minutes}`
    },
    
    calculateCurrentWorkingHours(punchInTime) {
      if (!punchInTime) return null
      
      const punchInDate = new Date(punchInTime)
      const now = new Date()
      const diffMs = now - punchInDate
      const hours = diffMs / (1000 * 60 * 60)
      
      return hours
    },
    
    formatWorkingHours(hours) {
      if (hours === null || hours === undefined) return 'N/A'
      
      const wholeHours = Math.floor(hours)
      const minutes = Math.floor((hours - wholeHours) * 60)
      
      if (wholeHours > 0) {
        return `${wholeHours}h ${minutes}m`
      }
      return `${minutes}m`
    },
    
    openPunchOutModal() {
      if(!this.isLoggedIn()) {
        return
      }
      if (!this.isPunchedIn) return
      
      // Get punch in time
      let punchInTime = null
      if (this.todayPunchRecord) {
        const punchInDetails = this.todayPunchRecord.punch_details?.punch_in_details || {}
        punchInTime = punchInDetails.punch_in_time
      } else {
        const punchInDataString = localStorage.getItem('punchInData')
        let punchInData = null
        try {
          punchInData = punchInDataString ? JSON.parse(punchInDataString) : null
        } catch (e) {
          console.error('Failed to parse punchInData from localStorage:', e)
        }
        punchInTime = punchInData?.punch_in_time || punchInData?.punchInTime || null
      }
      
      if (!punchInTime) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Punch in time not found. Please refresh the page.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }
      
      // Calculate current working hours
      const currentHours = this.calculateCurrentWorkingHours(punchInTime)
      const formattedHours = this.formatWorkingHours(currentHours)
      
      // Format punch in time in IST
      const punchInDateIST = this.convertToIST(punchInTime)
      const hours = String(punchInDateIST.getHours()).padStart(2, '0')
      const minutes = String(punchInDateIST.getMinutes()).padStart(2, '0')
      const punchInTimeFormatted = `${hours}:${minutes}`
      
      // Determine warning message and variant
      let warningTitle = null
      let warningMessage = null
      let warningVariant = 'warning'
      let hoursClass = 'text-success'
      
      if (currentHours < 4.5) {
        warningTitle = '⚠️ Absent Warning'
        warningMessage = `You have worked less than 4.5 hours. This will be marked as ABSENT and you will need to regularize your attendance.`
        warningVariant = 'danger'
        hoursClass = 'text-danger'
      } else if (currentHours < 7) {
        warningTitle = '⚠️ Half Day Warning'
        warningMessage = `You have worked less than 7 hours. This will be marked as HALF DAY.`
        warningVariant = 'warning'
        hoursClass = 'text-warning'
      } else {
        hoursClass = 'text-success'
      }
      
      this.punchOutModalData = {
        punchInTime: punchInTime,
        punchInTimeFormatted: punchInTimeFormatted,
        currentWorkingHours: formattedHours,
        currentHours: currentHours,
        warningTitle: warningTitle,
        warningMessage: warningMessage,
        warningVariant: warningVariant,
        hoursClass: hoursClass,
      }
      
      this.$refs['modal-punch-out-confirm'].show()
    },
    
    resetPunchOutModal() {
      this.punchOutModalData = null
    },
    
    async handlePunchOutConfirm() {
      if (this.isPunchOutLoading) return
      this.isPunchOutLoading = true
      try {
        const success = await this.handlePunchOut()
        if (success) {
          this.$refs['modal-punch-out-confirm'].hide()
        }
      } finally {
        this.isPunchOutLoading = false
      }
    },
    
    getPunchInTooltip() {
      if (this.isLoading) {
        return 'Loading...'
      }
      if (this.isPunchedIn) {
        if (this.punchInValidation) {
          const validationText = this.punchInValidation.isValid ? 'Valid Punch' : 'Invalid Punch'
          const timeText = this.timeSincePunchIn ? ` (${this.timeSincePunchIn} ago)` : ''
          return `${validationText}${timeText}`
        }
        return 'Already punched in today'
      }
      if (this.isCompanyWfhToday) {
        return this.companyWfhTodayTitle || 'Company-Wide WFH Day — punch in is still required'
      }
      return 'Punch In'
    },
    
    getPunchOutSummaryTooltip() {
      let tooltip = 'Attendance completed for today'
      if (this.punchOutTimeFormatted) {
        tooltip += `\nPunch Out: ${this.punchOutTimeFormatted} IST`
      }
      if (this.todayWorkingHours) {
        tooltip += `\nWorking Hours: ${this.todayWorkingHours}`
      }
      return tooltip
    },
    async getCurrentLocation() {
      return new Promise((resolve, reject) => {
        // Check permission before requesting location
        if (!this.$can('create', 'punch_attendance')) {
          reject(new Error('You do not have permission to access punch attendance'))
          return
        }

        // If in development mode and using mock location (IP address), return mock location immediately
        if (this.isDevelopmentMode && this.useMockLocation) {
          const mockLocation = this.officeLocation || {
            latitude: 18.520795767533105,
            longitude: 73.93895861408127,
            radius: 500
          }
          resolve({
            latitude: mockLocation.latitude,
            longitude: mockLocation.longitude,
            accuracy: 10, // Mock accuracy within valid range for testing
          })
          return
        }

        if (!navigator.geolocation) {
          // In development, fallback to mock location
          if (this.isDevelopmentMode) {
            console.warn('Geolocation not supported. Using mock location for testing.')
            const mockLocation = this.officeLocation || {
              latitude: 18.520795767533105,
              longitude: 73.93895861408127,
              radius: 500
            }
            resolve({
              latitude: mockLocation.latitude,
              longitude: mockLocation.longitude,
              accuracy: 10,
            })
            return
          }
          reject(new Error('Geolocation is not supported by this browser'))
          return
        }

        // Check if we're in development environment
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname === '[::1]'
        const isDevelopment = process.env.NODE_ENV === 'development'
        const isSecureContext = window.isSecureContext || window.location.protocol === 'https:'

        // If not secure context and not localhost, provide helpful error or use mock in dev mode
        if (!isSecureContext && !isLocalhost) {
          if (this.isDevelopmentMode) {
            // In development mode, use mock location instead of rejecting
            console.warn('🔧 Development Mode: Non-secure context detected. Using mock location for testing.')
            const mockLocation = this.officeLocation || {
              latitude: 18.520795767533105,
              longitude: 73.93895861408127,
              radius: 500
            }
            resolve({
              latitude: mockLocation.latitude,
              longitude: mockLocation.longitude,
              accuracy: 10,
            })
            return
          }
          reject(new Error(
            'Geolocation requires a secure context (HTTPS) or localhost. ' +
            'Please use HTTPS or test on localhost. ' +
            'For local testing, you can use: https://localhost:8080'
          ))
          return
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
            })
          },
          (error) => {
            // Provide more helpful error messages
            let errorMessage = 'Failed to get location'
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = 'Location permission denied. Please allow location access in your browser settings.'
                break
              case error.POSITION_UNAVAILABLE:
                errorMessage = 'Location information is unavailable. Please check your GPS/network connection.'
                break
              case error.TIMEOUT:
                errorMessage = 'Location request timed out. Please try again.'
                break
              default:
                errorMessage = error.message || 'Unknown geolocation error'
            }

            // In development, provide mock location for testing
            if (this.isDevelopmentMode) {
              console.warn('🔧 Development Mode: Geolocation error. Using mock location for testing.')
              console.warn('Error details:', error)
              
              // Use office location as mock for testing
              const mockLocation = this.officeLocation || {
                latitude: 18.520795767533105,
                longitude: 73.93895861408127,
                radius: 500
              }
              
              resolve({
                latitude: mockLocation.latitude,
                longitude: mockLocation.longitude,
                accuracy: 10, // Mock accuracy within valid range
              })
              return
            }

            reject(new Error(errorMessage))
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        )
      })
    },

    async handlePunchIn() {
      // Fix 8: guard against punch-out-done state at very top
      if (this.isPunchOutDone) return
      // Fix 16: permission check before any geolocation or API work
      if (!this.$can('create', 'punch_attendance')) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: "You don't have permission to record attendance.",
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return
      }
      // Fix 9: validate emp_id before API calls
      if (!this.userData?.emp_id) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Employee data not found. Please reload.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return
      }
      // Fix 15: track pre-existing punch state so catch block only rolls back what we changed
      const wasAlreadyPunchedIn = this.isPunchedIn
      try {
        // Fix 12: re-fetch WFH status right before computing isWFH to catch late approvals
        await this.checkWFHLeaveForToday()
        if (!this._isMounted) return

        // Validate device type is allowed
        const deviceValidation = this.validateDeviceType()
        if (!deviceValidation.isValid) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Device Not Allowed',
              text: deviceValidation.message,
              icon: 'XCircleIcon',
              variant: 'danger',
            },
          })
          return
        }

        // Fix 2: align isWFH with validateLocation — allow_wfm, personal WFH, or Co. WFH
        const metadata = this.punchConfig?.metadata?.metadata || this.punchConfig?.metadata || {}
        const isWFH = this.isWFHToday || this.isCompanyWfhToday || metadata.allow_wfm === 'TRUE'

        // Location capture is mandatory for all users (including WFH); WFH only skips geofencing
        const location = await this.getCurrentLocation()
        if (!this._isMounted) return

        if (!location || typeof location.latitude !== 'number' || typeof location.longitude !== 'number') {
          throw new Error('Failed to capture location. Please ensure location permissions are enabled and try again.')
        }

        // Fix 10: null/undefined accuracy is a safe pass-through (don't block punch-in)
        if (!isWFH && typeof location.accuracy === 'number') {
          const accuracyValidation = this.validateAccuracy(location.accuracy)
          if (!accuracyValidation.isValid) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Location Accuracy Too Low',
                text: accuracyValidation.message,
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
            return
          }
        } else if (!isWFH && location.accuracy == null) {
          // Accuracy unavailable; pass through without blocking punch-in
          console.warn('Location accuracy not available; skipping accuracy validation.')
        }

        // validateLocation skips geofencing for WFH but still records real distance
        const locationValidation = this.validateLocation(location)

        // GEOFENCING: Block punch-in if employee is outside the office premises
        if (!locationValidation.isValid) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Punch In Blocked - Outside Office Premises',
              text: `You are outside the office premises. Punch-in is not allowed. ` +
                `You are ${Math.round(locationValidation.distance)}m away from the office ` +
                `(allowed radius: ${this.officeLocation?.radius}m).`,
              icon: 'XCircleIcon',
              variant: 'danger',
            },
          })
          return
        }

        // Fix 4: capture timestamp AFTER geolocation (which can take up to 10s)
        const currentTime = new Date().toISOString()

        const punchData = {
          emp_id: this.userData?.emp_id || null,
          punch_in_time: currentTime,
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
          device_info: {
            ...this.deviceInfo,
            // IP address will be captured on backend from request headers
          },
          location_validation: {
            isValid: locationValidation.isValid,
            distance: locationValidation.distance,
            officeLocation: this.officeLocation,
          },
        }


        const apiResponse = await punchAttendanceService.punchIn(punchData)

        if (apiResponse.status === 'OK' && apiResponse.data) {
          // Fix 5: update all state BEFORE the _isMounted guard so the server record is
          // always reflected even if the component is mid-unmount
          this.isPunchedIn = true
          this.todayPunchRecord = apiResponse.data
          const punchInDetails = apiResponse.data.punch_details?.punch_in_details || {}
          const storedPunchInTime = punchInDetails.punch_in_time || punchData.punch_in_time
          this.punchInValidation = punchInDetails.location_validation || locationValidation

          this.updateTimeSincePunchIn(storedPunchInTime)
          this.formatPunchInTime(storedPunchInTime)

          // Fix 6: write punchInData to localStorage (same shape as checkTodayPunchStatus)
          // so openPunchOutModal can find it reliably
          localStorage.setItem('punchInData', JSON.stringify({
            id: apiResponse.data.id,
            emp_id: apiResponse.data.emp_id,
            punch_in_time: storedPunchInTime,
            location_validation: this.punchInValidation,
          }))

          if (!this._isMounted) return

          // Fix 11: use server-confirmed time (via punchInTimeFormatted) for the toast
          const displayTime = this.punchInTimeFormatted || punchData.punch_in_time
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Punch In Successful! ✅',
              text: `Punched in at ${displayTime} IST.`,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })

        } else {
          throw new Error(apiResponse.message || 'Failed to save punch in to server')
        }
      } catch (error) {
        console.error('Error during punch in:', error)
        // Fix 15: only rollback state that this attempt set — don't clear pre-existing punch
        if (!wasAlreadyPunchedIn) {
          this.isPunchedIn = false
          this.punchInValidation = null
          this.punchInTimeFormatted = null
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Punch In Failed',
            text: error.message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    },

    async handlePunchOut() {
      if (this.isLoading) return false
      this.isLoading = true
      try {
        // Validate device type is allowed
        const deviceValidation = this.validateDeviceType()
        if (!deviceValidation.isValid) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Device Not Allowed',
              text: deviceValidation.message,
              icon: 'XCircleIcon',
              variant: 'danger',
            },
          })
          return false
        }

        // Fix 2: align isWFH with validateLocation — allow_wfm, personal WFH, or Co. WFH
        const metadata = this.punchConfig?.metadata?.metadata || this.punchConfig?.metadata || {}
        const isWFH = this.isWFHToday || this.isCompanyWfhToday || metadata.allow_wfm === 'TRUE'

        // Location capture is mandatory for all users (including WFH); WFH only skips geofencing
        const location = await this.getCurrentLocation()
        if (!this._isMounted) return false

        if (!location || typeof location.latitude !== 'number' || typeof location.longitude !== 'number') {
          throw new Error('Failed to capture location. Please ensure location permissions are enabled and try again.')
        }

        // Accuracy validation (skip for WFH); null accuracy is a safe pass-through
        if (!isWFH && typeof location.accuracy === 'number') {
          const accuracyValidation = this.validateAccuracy(location.accuracy)
          if (!accuracyValidation.isValid) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Location Accuracy Too Low',
                text: accuracyValidation.message,
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
            return false
          }
        } else if (!isWFH && location.accuracy == null) {
          console.warn('Location accuracy not available; skipping accuracy validation.')
        }

        // Get punch in data from API record (required for calculating hours and API update)
        let storedPunchInTime = null
        if (this.todayPunchRecord) {
          const punchInDetails = this.todayPunchRecord.punch_details?.punch_in_details || {}
          storedPunchInTime = punchInDetails.punch_in_time
        }

        // Block punch out if punch-in time is not found
        if (!storedPunchInTime) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Punch Out Blocked',
              text: 'Punch-in time not found. Please contact HR.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          return false
        }

        // validateLocation skips geofencing for WFH but still records real distance
        const locationValidation = this.validateLocation(location)

        if (!locationValidation.isValid) {
          const errorMessage = `Punch Out Blocked: You are ${Math.round(locationValidation.distance)}m away from the office location. ` +
            `Punch out is only allowed within ${this.officeLocation?.radius}m of ${this.officeLocation?.name || 'the office'}. ` +
            `Please move to the office premises to punch out.`

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Punch Out Blocked - Outside Office Premises',
              text: errorMessage,
              icon: 'XCircleIcon',
              variant: 'danger',
            },
          })
          return false
        }

        // Fix 4: capture timestamp AFTER geolocation (which can take up to 10s)
        const currentTime = new Date().toISOString()

        const punchOutData = {
          emp_id: this.userData?.emp_id || null,
          punch_out_time: currentTime,
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
          device_info: {
            ...this.deviceInfo,
            // IP address will be captured on backend from request headers
          },
          location_validation: {
            isValid: locationValidation.isValid,
            distance: locationValidation.distance,
            officeLocation: this.officeLocation,
          },
        }

        // Calculate total hours worked
        const punchInTimeObj = new Date(storedPunchInTime)
        const punchOutTimeObj = new Date(currentTime)
        const diffMs = punchOutTimeObj - punchInTimeObj
        const totalHours = (diffMs / (1000 * 60 * 60)).toFixed(2)
        const hoursCompleted = parseFloat(totalHours)


        punchOutData.hours_completed = hoursCompleted

        const apiResponse = await punchAttendanceService.punchOut(punchOutData)

        if (apiResponse.status === 'OK' && apiResponse.data) {
          // Fix 5: update all state BEFORE the _isMounted guard so the server record is
          // always reflected even if the component is mid-unmount
          this.todayWorkingHours = this.formatWorkingHours(hoursCompleted)
          this.formatPunchOutTime(currentTime)
          this.isPunchedIn = false
          this.isPunchOutDone = true
          this.todayPunchRecord = apiResponse.data
          this.punchInValidation = null
          this.timeSincePunchIn = null
          this.punchInTimeFormatted = null
          localStorage.removeItem('punchInData')
          if (this.timeSincePunchInInterval) {
            clearInterval(this.timeSincePunchInInterval)
            this.timeSincePunchInInterval = null
          }

          if (!this._isMounted) return false

          // Show success toast with IST time
          const punchOutTimeIST = this.convertToIST(currentTime)
          const hours = String(punchOutTimeIST.getHours()).padStart(2, '0')
          const minutes = String(punchOutTimeIST.getMinutes()).padStart(2, '0')
          const formattedTime = `${hours}:${minutes}`
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Punch Out Successful! ✅',
              text: `Punched out at ${formattedTime} IST. Hours completed: ${this.todayWorkingHours} (${totalHours} hours).`,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })


          return true
        } else {
          throw new Error(apiResponse.message || 'Failed to save punch out to server')
        }
      } catch (error) {
        console.error('Error during punch out:', error)
        // Keep punched-in state since the API failed
        this.isPunchedIn = true
        this.isPunchOutDone = false
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Punch Out Failed',
            text: `Failed to save punch out: ${error.message || 'Unknown error'}. Please try again.`,
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return false
      } finally {
        this.isLoading = false
      }
    },
  },
  beforeDestroy() {
    this._isMounted = false
    // Clear interval on component destroy
    if (this.timeSincePunchInInterval) {
      clearInterval(this.timeSincePunchInInterval)
    }
  },
}
</script>

<style scoped>
.punch-attendance-buttons {
  margin-right: 0.5rem;
  gap: 0.5rem;
}

.punch-in-btn {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  font-weight: 600;
  color: #00AC99 !important; /* Teal color */
  border: 1px solid #1d8478;
  border-radius: 6px;
  background-color: rgba(0, 172, 153, 0.08);
  box-shadow: 0 2px 4px rgba(0, 172, 153, 0.15);
}

.punch-in-btn:not(.disabled):hover {
  color: #008B7D !important; /* Darker teal on hover */
  border-color: #008B7D;
  background-color: rgba(0, 172, 153, 0.15);
  box-shadow: 0 4px 8px rgba(0, 172, 153, 0.25);
  transform: translateY(-1px);
}

/* When disabled (punched in), remove all button styling - make it look like a status indicator */
.punch-in-btn.disabled {
  cursor: default;
  pointer-events: none;
  border: none !important;
  background-color: transparent !important;
  box-shadow: none !important;
  padding: 0.5rem 0.75rem; /* Slightly less padding when disabled */
  font-weight: 500; /* Lighter font weight */
}

.punch-in-btn.punch-in-valid {
  color: #28c76f !important; /* Light green for valid */
}

.punch-in-btn.punch-in-valid.disabled {
  border: none !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

.punch-in-btn.punch-in-valid:hover {
  color: #22b863 !important; /* Slightly darker light green on hover */
}

.punch-in-btn.punch-in-invalid {
  color: #dc3545 !important; /* Red for invalid */
}

.punch-in-btn.punch-in-invalid.disabled {
  border: none !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

.punch-in-btn.punch-in-invalid:hover {
  color: #c82333 !important;
}

.punch-out-btn {
  display: flex;
  align-items: center;
  padding: 0.28rem 0.45rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  color: #e8b483 !important; /* Danger lighter shade - brighter */
  border: 1px solid #ff9f43;
  border-radius: 4px;
  background-color: rgba(255, 159, 67, 0.1);
  box-shadow: 0 1px 2px rgba(255, 159, 67, 0.15);
}

.punch-out-btn .punch-out-icon >>> svg,
.punch-out-btn .punch-out-icon .feather {
  width: 14px !important;
  height: 14px !important;
}

.punch-out-btn:not(.disabled):hover {
  color: #ff8c2e !important;
  border-color: #ff8c2e;
  background-color: rgba(255, 159, 67, 0.2);
  box-shadow: 0 2px 4px rgba(255, 159, 67, 0.25);
}

.punch-out-btn.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
  box-shadow: none;
}

.punch-in-btn .feather-icon,
.punch-out-btn .feather-icon {
  color: inherit !important;
}

/* Override text-primary and text-danger classes when buttons have borders */
.punch-in-btn .text-primary {
  color: inherit !important;
}

.punch-out-btn .text-danger {
  color: inherit !important;
}

.punch-in-time {
  font-size: 1rem;
  font-weight: 700;
  opacity: 1;
  color: #00AC99 !important;
}

.punch-out-summary {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  cursor: default;
  pointer-events: auto; /* Allow hover for tooltip */
  border: none !important;
  background-color: transparent !important;
  box-shadow: none !important;
  font-weight: 500;
}

.punch-out-time {
  font-size: 1rem;
  font-weight: 700;
  opacity: 1;
  color: #28c76f !important;
}

.working-hours {
  font-size: 0.9rem;
  font-weight: 600;
  opacity: 1;
  color: #7367f0 !important;
  padding: 0.2rem 0.5rem;
  background-color: rgba(115, 103, 240, 0.1);
  border-radius: 4px;
}

.company-wfh-badge-nav {
  cursor: default !important;
  pointer-events: auto;
  opacity: 0.95;
}

.company-wfh-badge-nav .text-info {
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.punch-out-details {
  font-size: 0.95rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.detail-row:last-child {
  border-bottom: none;
}

/* Mobile: keep punch controls on one tidy row (icon + time; hide long labels) */
@media (max-width: 767px) {
  .punch-attendance-buttons {
    margin-right: 0.2rem;
    gap: 0.2rem;
    flex-wrap: nowrap;
    max-width: 100%;
    min-width: 0;
  }

  .punch-attendance-buttons >>> .nav-item {
    float: none;
    margin: 0;
  }

  .punch-attendance-buttons >>> .nav-link,
  .punch-in-btn,
  .punch-out-summary,
  .company-wfh-badge-nav {
    padding: 0.1rem 0.18rem !important;
    font-size: 0.65rem;
    white-space: nowrap;
    min-height: 0;
    border-radius: 4px;
  }

  .punch-in-btn:not(.disabled) {
    box-shadow: none;
  }

  .punch-out-btn {
    padding: 0.08rem 0.15rem !important;
    font-size: 0.6rem;
    min-height: 0;
    border-radius: 3px;
    box-shadow: none;
  }

  .punch-label-text {
    display: none;
  }

  .punch-in-btn .mr-50,
  .punch-out-btn .mr-50,
  .punch-out-summary .mr-50,
  .company-wfh-badge-nav .mr-50 {
    margin-right: 0.15rem !important;
  }

  /* Shrink punch icons on mobile navbar (bell icon is 21px) */
  .punch-in-btn >>> svg,
  .punch-out-summary >>> svg,
  .company-wfh-badge-nav >>> svg,
  .punch-in-btn .feather,
  .punch-out-summary .feather,
  .company-wfh-badge-nav .feather,
  .punch-in-btn .punch-nav-icon >>> svg {
    width: 11px !important;
    height: 11px !important;
  }

  .punch-out-btn >>> svg,
  .punch-out-btn .feather,
  .punch-out-btn .punch-nav-icon >>> svg {
    width: 10px !important;
    height: 10px !important;
  }

  .punch-in-time,
  .punch-out-time {
    font-size: 0.7rem;
    margin-left: 0.15rem !important;
  }

  .working-hours {
    font-size: 0.65rem;
    padding: 0.08rem 0.25rem;
  }
}
</style>

